import React, { useRef, useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import ProductCarouselSkeleton from "../../Components/ProductCarouselSkeleton";
import { addProductImg } from "../../Reducers/authReducer";
import GradeIcon from "@mui/icons-material/Grade";
import { useGetUserDataQuery } from "../../services/login";
import formatPrice from "../formatPrice";
import ProductCountries from "../Header/ProductCountries";
import FlashOnIcon from "@mui/icons-material/FlashOn";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import "./homeCarousel.scss";

var moment = require("moment");

const storeIds = [1, 3, 18, 19, 20, 21, 22, 23, 25, 11, 12, 26];

function HomeProductCards({ productData, heading, isLoading }) {
  const { t } = useTranslation();
  const { userId, userToken, countryCode } = useSelector((state) => state.auth);
  const dispatch = useDispatch();

  const { data: userData } = useGetUserDataQuery(
    { userId: userId, userToken: userToken, countryCode: countryCode },
    { count: 1 },
    { refetchOnMountOrArgChange: true }
  );

  const trackRef = useRef(null);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);

  function productTitle(title) {
    return title
      ?.replace(/[^A-Za-z0-9]/g, " ")
      ?.replace(/[^\w\s]/gi, " ")
      ?.replace(/ /g, "-");
  }

  function addProductImage(data) {
    dispatch(addProductImg({ productImg: data }));
  }

  const handleError = (e) => {
    console.error("Error loading image:", e.target.src);
    e.target.onerror = null;
    e.target.src = "/Image-not-found.jpg";
  };

  function getDeliverydays(delivery) {
    const deliveryDaya = Number(delivery) + Number(userData?.contant?.delivery_days || 0);
    if (Number(deliveryDaya) > 0) {
      return `${t("delivery_by")} ${moment().add(14, "days").format("Do MMM")}`;
    } else {
      return `${t("delivery_by")} ${moment().add(14, "days").format("Do MMM")}`;
    }
  }

  function functionIdLink(id, pid, store_id) {
    if (storeIds?.includes(Number(store_id))) {
      return pid;
    } else {
      return id;
    }
  }

  // Discount %, worked out from the two prices the card already shows.
  function discountPercent(prices) {
    const was = Number(prices?.price);
    const now = Number(prices?.value);
    if (!prices?.deal_value || !Number.isFinite(was) || !Number.isFinite(now)) return 0;
    if (was <= 0 || now >= was) return 0;
    return Math.round(((was - now) / was) * 100);
  }

  // Keep the arrow enabled/disabled state in sync with actual scroll position.
  const updateArrows = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;
    setCanScrollPrev(el.scrollLeft > 4);
    setCanScrollNext(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  }, []);

  useEffect(() => {
    updateArrows();
    const el = trackRef.current;
    if (!el) return undefined;
    el.addEventListener("scroll", updateArrows, { passive: true });
    window.addEventListener("resize", updateArrows);
    return () => {
      el.removeEventListener("scroll", updateArrows);
      window.removeEventListener("resize", updateArrows);
    };
  }, [updateArrows, productData]);

  const handleScroll = (direction) => {
    const el = trackRef.current;
    if (!el) return;
    const distance = el.clientWidth * 0.9;
    el.scrollBy({ left: direction === "next" ? distance : -distance, behavior: "smooth" });
  };

  if (!isLoading && !(productData?.length > 0)) return null;

  return (
    <div className="hp-carousel">
      <h2 className="hp-carousel__heading">{heading}</h2>

      {isLoading ? (
        <ProductCarouselSkeleton />
      ) : (
        <div className="hp-carousel__viewport">
          <button
            type="button"
            className="hp-carousel__arrow hp-carousel__arrow--prev"
            onClick={() => handleScroll("prev")}
            disabled={!canScrollPrev}
            aria-label={t("previous", "Previous")}
          >
            <ChevronLeftIcon />
          </button>
          <button
            type="button"
            className="hp-carousel__arrow hp-carousel__arrow--next"
            onClick={() => handleScroll("next")}
            disabled={!canScrollNext}
            aria-label={t("next", "Next")}
          >
            <ChevronRightIcon />
          </button>

          <div className="hp-carousel__track" ref={trackRef}>
            {productData?.map((item, i) => {
              const isCheckPrice = Number(item?.prices?.value) === 0;
              const hasFlashDeal = Boolean(item?.prices?.deal_value);
              const off = discountPercent(item?.prices);

              return (
                <Link
                  key={i}
                  className="hp-card"
                  onClick={() => addProductImage(item?.image)}
                  to={`/product/${item?.store_id}/${functionIdLink(item?.id, item?.pid, item?.store_id)}/${productTitle(item?.title)}?country=${item?.country}`}
                >
                  <div className="hp-card__media">
                    {hasFlashDeal && (
                      <div className="hp-card__badges">
                        <span className="hp-card__badge">
                          <FlashOnIcon style={{ fontSize: 14 }} />
                          {t("deal", "Deal")}
                        </span>
                      </div>
                    )}
                    <img
                      className="hp-card__img"
                      src={item?.image}
                      alt={item?.title || ""}
                      loading="lazy"
                      decoding="async"
                      onError={handleError}
                    />
                  </div>

                  <div className="hp-card__body">
                    <div className="hp-card__brand">{item?.brand}</div>

                    <h3 className="hp-card__title" title={item?.title}>
                      {item?.title?.replace(/[^\w\s.,'/:]/gi, " ")}
                    </h3>

                    <div className="hp-card__rating">
                      {item?.rating && Number(item?.rating) > 0 && (
                       <div style={{display: 'flex', alignItems: 'center', gap: '5px'}}>
                          <GradeIcon />
                          {item?.rating}
                        </div>
                      )}
                        <span className="hp-card__meta">
                        <span>{t("from")}</span>
                        <span className="hp-card__unit">
                          <img
                            className="hp-card__flag"
                            loading="lazy"
                            src={`https://flagcdn.com/w20/${item?.country?.toLowerCase()}.png`}
                            srcSet={`https://flagcdn.com/w40/${item?.country?.toLowerCase()}.png 2x`}
                            alt=""
                          />
                          {ProductCountries(item?.country)}
                        </span>
                      </span>
                    </div>

                    <div className="hp-card__price">
                      {isCheckPrice ? (
                        <span className="hp-card__pill">Check price</span>
                      ) : (
                        <>
                          <span className={`hp-card__now${hasFlashDeal ? " is-deal" : ""}`}>
                            {formatPrice(item?.prices?.value, item?.prices?.currency, 0)}
                          </span>
                          {hasFlashDeal && (
                            <s className="hp-card__was">
                              {formatPrice(item?.prices?.price, item?.prices?.currency, 0)}
                            </s>
                          )}
                          {off > 0 && <span className="hp-card__off">{`-${off}%`}</span>}
                        </>
                      )}
                    </div>

                    <div className="hp-card__foot">
                      <span className="hp-card__meta hp-card__meta--delivery">
                        <LocalShippingOutlinedIcon style={{ fontSize: 15 }} />
                        <span>{getDeliverydays(item.estimated_delivery)}</span>
                      </span>
                    
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export default HomeProductCards;
